import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import {
    Box,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Typography,
    Avatar,
    Collapse,
    Menu,
    MenuItem,
    Radio,
    IconButton,
    Badge,
    Divider
} from '@mui/material';
import {
    Settings,
    MenuOpen,
    ExpandLess,
    ExpandMore,
    Logout,
    Home as HomeIcon,
    ChatBubble as MessageIcon,
    CalendarMonth,
    AttachMoney,
    Info,
    Dashboard,
    Assignment,
    Hotel,
    Folder,
    Group,
    BarChart,
    DirectionsBus,
    History,
    Person,
    Flight,
    GridOn,
    FilterList,
    Inbox,
    Business,
    Storefront
} from '@mui/icons-material';

import { useTheme } from '@mui/material/styles';
import { useTheme as useAppTheme } from '../contexts/ThemeContext';
import { auth, db, functions } from '../firebaseConfig';
import { httpsCallable } from 'firebase/functions';
import { signOut } from 'firebase/auth';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { useArtistsWithDateRange, useArtistsEventsBasic, usePendingEventInvites } from '../contexts/ArtistContext';
import moment from 'moment-timezone';
import { getCalendarRange, EventTypes, EventType, EventTypeGroup, getEventGroup, EventStatus, EventStatuses } from '../constants/common';
import { getEventSections } from '../constants/eventSections';
import appVersion from '../version.json';
import packageInfo from '../../package.json';

const getEventIcon = (type) => {
    const eventType = EventTypes.find(t => t.value === type);
    return eventType ? eventType.icon : <Info fontSize="inherit" />;
};

const getStatusColor = (status) => {
    switch (status) {
        case 'CONFIRMED':
        case 'e8334469-8390-4824-9b57-619277a1127b':
            return '#4caf50'; // Green
        case 'PENDING':
            return '#ff9800'; // Orange
        case 'CANCELLED':
            return '#f44336'; // Red
        case 'ON_HOLD':
            return '#9c27b0'; // Purple
        case 'DRAFT':
            return '#757575'; // Grey
        default:
            return '#757575';
    }
};

const formatVenueInfo = (event) => {
    const eventTitle = event.name || event.venue?.name || 'Event';

    let locationParts = [];
    if (event.venue?.name && event.venue.name !== eventTitle) {
        locationParts.push(event.venue.name);
    }

    const city = event.venue?.location?.city || event.venue?.eventVenueLocation?.city;
    if (city) locationParts.push(city);

    if (locationParts.length > 0) {
        return `${eventTitle} (${locationParts.join(', ')})`;
    }
    return eventTitle;
};

const DRAWER_WIDTH = 250;

const Sidebar = ({ collapsed, onCollapse, onExpand, menuItems, title, onTitleClick }) => {
    const theme = useTheme();
    const { mode, setTheme } = useAppTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const { eventId: activeEventId } = useParams();

    // Data Fetching (Only needed if NOT using custom menuItems)
    const [dateRange] = useState(getCalendarRange());

    // Conditionally fetch artists only if we are in "Global" mode (no menuItems)
    const { data: artists } = useArtistsEventsBasic(dateRange.start, dateRange.end, !menuItems);

    // Pending event-invite counts, for badges on the artist/event this user was invited to (upcoming dates only)
    const { data: pendingInvites } = usePendingEventInvites();
    const pendingInvitesByEventId = React.useMemo(() => {
        const map = {};
        const today = moment().startOf('day');
        (pendingInvites || []).forEach(invite => {
            if (!invite?.event?.date || moment(invite.event.date).isBefore(today, 'day')) return;
            map[invite.event.id] = (map[invite.event.id] || 0) + 1;
        });
        return map;
    }, [pendingInvites]);
    const pendingInvitesByArtistId = React.useMemo(() => {
        const map = {};
        const today = moment().startOf('day');
        (pendingInvites || []).forEach(invite => {
            if (!invite?.event?.date || moment(invite.event.date).isBefore(today, 'day')) return;
            const artistId = invite.event.artist?.id;
            if (artistId) map[artistId] = (map[artistId] || 0) + 1;
        });
        return map;
    }, [pendingInvites]);

    const renderInviteBadge = (count) => {
        if (!count) return null;
        return (
            <Box
                component="span"
                sx={{
                    ml: 1,
                    px: 0.6,
                    height: 18,
                    minWidth: 18,
                    borderRadius: '9px',
                    bgcolor: 'error.main',
                    color: '#fff',
                    fontSize: '0.68rem',
                    fontWeight: 700,
                    lineHeight: 1,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
                }}
            >
                {count}
            </Box>
        );
    };

    const [selectedArtistId, setSelectedArtistId] = useState(null);
    const [expandedArtistId, setExpandedArtistId] = useState(null);
    const [expandedTourIds, setExpandedTourIds] = useState({});
    const [expandedGroupNames, setExpandedGroupNames] = useState({});
    const [showPreviousEvents, setShowPreviousEvents] = useState({});
    const [eventTypeFilter, setEventTypeFilter] = useState('ALL');
    const [tourFilter, setTourFilter] = useState(null); // null = all tours, tourId = specific
    const [filterAnchorEl, setFilterAnchorEl] = useState(null);

    const handleFilterClick = (event) => {
        event.stopPropagation();
        setFilterAnchorEl(event.currentTarget);
    };

    const handleFilterClose = (filterType) => {
        if (filterType) {
            setEventTypeFilter(filterType);
        }
        setFilterAnchorEl(null);
    };

    const handleTourFilterChange = (tourId) => {
        setTourFilter(tourId);
        setFilterAnchorEl(null);
    };

    const [anchorElUser, setAnchorElUser] = useState(null);
    const [anchorElTheme, setAnchorElTheme] = useState(null);
    const [unreadCount, setUnreadCount] = useState(0);
    const [myOrgs, setMyOrgs] = useState([]);

    // Organizations the user belongs to: links to the workspace (/org) and promoter/venue shows (/partner)
    useEffect(() => {
        if (!auth.currentUser) return;
        httpsCallable(functions, 'listMyOrganizations')()
            .then((res) => setMyOrgs(res.data.organizations || []))
            .catch(() => setMyOrgs([]));
    }, []);

    // Messaging Unread Listener
    useEffect(() => {
        if (!auth.currentUser) return;

        const q = query(
            collection(db, 'conversations'),
            where('participants', 'array-contains', auth.currentUser.uid)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            let count = 0;
            snapshot.docs.forEach(doc => {
                const data = doc.data();
                if (data.lastMessage && !data.lastMessage.readBy?.includes(auth.currentUser.uid)) {
                    count++;
                }
            });
            setUnreadCount(count);
        });

        return () => unsubscribe();
    }, [auth.currentUser]);

    // Sync selectedArtistId from URL
    useEffect(() => {
        // If query param 'menuItems' is explicitly passed, maybe we respect it, 
        // but user requested ONE sidebar. So we prioritize the global logic if we want.
        // However, we will just rely on NestedLayouts NOT passing menuItems.

        if (menuItems) return;

        if (!artists) return;

        const pathParts = location.pathname.split('/');

        // 1. /artist/:artistId...
        if (pathParts[1] === 'artist' && pathParts[2]) {
            const artistId = pathParts[2];
            setSelectedArtistId(artistId);
            setExpandedArtistId(artistId);

            // If /tours/:tourId
            if (pathParts[3] === 'tours' && pathParts[4]) {
                const tourId = pathParts[4];
                setExpandedTourIds(prev => ({ ...prev, [tourId]: true }));
            }
        }

        // 2. /event/:eventId...
        else if (pathParts[1] === 'event' && pathParts[2]) {
            const eventId = pathParts[2];
            // Find which artist/tour owns this event
            for (const artist of artists) {
                const artistEvents = artist.events_on_artist || [];
                // Check if event is in this artist's list (tour or standalone)
                // Just linear search
                const foundEvent = artistEvents.find(e => e.id === eventId);
                if (foundEvent) {
                    setSelectedArtistId(artist.id);
                    setExpandedArtistId(artist.id);
                    if (foundEvent.tour) {
                        setExpandedTourIds(prev => ({ ...prev, [foundEvent.tour.id]: true }));
                    }

                    // Auto-expand "Previous Shows" if this is a past event
                    const today = moment().startOf('day');
                    if (moment(foundEvent.date).isBefore(today)) {
                        setShowPreviousEvents(prev => ({ ...prev, [artist.id]: true }));
                    }
                    break;
                }
            }
        } else {
            setSelectedArtistId(null);
        }
    }, [location.pathname, menuItems, artists]);

    const handleLogout = async () => {
        await signOut(auth);
        navigate("/login");
    };

    const handleArtistClick = (artistId) => {
        navigate(`/artist/${artistId}`);
        setExpandedArtistId(artistId);
    };

    const handleEventClick = (eventId) => {
        navigate(`/event/${eventId}`);
    };

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            bgcolor: mode === 'dark' ? '#12161d' : '#f0f4f9',
            borderRight: '1px solid',
            borderColor: mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'divider',
            width: '100%' // Filled by parent container
        }}>
            {/* Logo/Title Area */}
            {/* Logo/Title Area */}
            <Box sx={{
                p: collapsed ? 1 : 2,
                pt: { xs: 'calc(env(safe-area-inset-top) + 8px)', md: collapsed ? 2 : 2 },
                display: 'flex',
                alignItems: 'center',
                justifyContent: collapsed ? 'center' : 'space-between',
                flexDirection: collapsed ? 'column' : 'row',
                gap: collapsed ? 2 : 0
            }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        cursor: 'pointer',
                        justifyContent: collapsed ? 'center' : 'flex-start'
                    }}
                    onClick={collapsed && onExpand ? onExpand : (onTitleClick || (() => navigate('/')))}
                >
                    {(!title || collapsed) && (
                        <Avatar
                            src="/tourminator_icon.jpg"
                            alt="Tourminator"
                            variant="rounded"
                            sx={{ width: collapsed ? 40 : 32, height: collapsed ? 40 : 32 }}
                        />
                    )}
                    {!collapsed && (
                        <Typography variant="h6" fontWeight={600} color="text.primary" sx={{ letterSpacing: '-0.5px' }}>
                            {title || 'Tourminator'}
                        </Typography>
                    )}
                </Box>
                {onCollapse && !collapsed && (
                    <IconButton onClick={onCollapse} size="small" sx={{ color: 'text.secondary' }}>
                        <MenuOpen />
                    </IconButton>
                )}
            </Box>

            <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
                <List dense sx={{ px: 1 }}>
                    {/* CUSTOM MENU MODE */}
                    {menuItems ? (
                        <>
                            {menuItems.map((item, index) => (
                                <ListItem key={index} disablePadding sx={{ mb: 0.5 }}>
                                    <ListItemButton
                                        selected={item.active}
                                        onClick={item.onClick}
                                        sx={{
                                            borderRadius: 2,
                                            '&.Mui-selected': { bgcolor: mode === 'dark' ? 'rgba(255,255,255,0.1)' : '#dde3ea' }
                                        }}
                                    >
                                        {item.icon && <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>}
                                        <ListItemText
                                            primary={item.label}
                                            primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: item.active ? 600 : 500 }}
                                        />
                                    </ListItemButton>
                                </ListItem>
                            ))}
                        </>
                    ) : (
                        /* DEFAULT GLOBAL MODE WITH DEEP NAVIGATION */
                        <>
                            {/* Home Button */}
                            <ListItem disablePadding sx={{ mb: 1 }}>
                                <ListItemButton
                                    onClick={() => navigate('/')}
                                    sx={{ borderRadius: 2 }}
                                    selected={location.pathname === '/'}
                                >
                                    <ListItemIcon sx={{ minWidth: 40 }}><HomeIcon fontSize="small" /></ListItemIcon>
                                    <ListItemText primary="Home" primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 600 }} />
                                </ListItemButton>
                            </ListItem>

                            {/* My Tasks Button */}
                            <ListItem disablePadding sx={{ mb: 1 }}>
                                <ListItemButton
                                    onClick={() => navigate('/tasks')}
                                    sx={{ borderRadius: 2 }}
                                    selected={location.pathname === '/tasks'}
                                >
                                    <ListItemIcon sx={{ minWidth: 40 }}><Assignment fontSize="small" /></ListItemIcon>
                                    <ListItemText primary="My Tasks" primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 600 }} />
                                </ListItemButton>
                            </ListItem>

                            {/* Inbox Button */}
                            <ListItem disablePadding sx={{ mb: 1 }}>
                                <ListItemButton
                                    onClick={() => navigate('/inbox')}
                                    sx={{ borderRadius: 2 }}
                                    selected={location.pathname === '/inbox'}
                                >
                                    <ListItemIcon sx={{ minWidth: 40 }}><Inbox fontSize="small" /></ListItemIcon>
                                    <ListItemText primary="Innboks" primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 600 }} />
                                </ListItemButton>
                            </ListItem>

                            {/* Messaging Button */}
                            {artists?.length > 0 && (
                                <ListItem disablePadding sx={{ mt: 1 }}>
                                    <ListItemButton
                                        onClick={() => navigate('/messages')}
                                        sx={{ borderRadius: 2 }}
                                        selected={location.pathname.includes('/messages')}
                                    >
                                        <ListItemIcon sx={{ minWidth: 40 }}>
                                            <Badge badgeContent={unreadCount} color="error" overlap="circular">
                                                <MessageIcon fontSize="small" />
                                            </Badge>
                                        </ListItemIcon>
                                        <ListItemText primary="Messaging" primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 600 }} />
                                    </ListItemButton>
                                </ListItem>
                            )}

                            {myOrgs.length > 0 && (
                                <ListItem disablePadding sx={{ mt: 1 }}>
                                    <ListItemButton
                                        onClick={() => navigate('/org')}
                                        sx={{ borderRadius: 2 }}
                                        selected={location.pathname.startsWith('/org')}
                                    >
                                        <ListItemIcon sx={{ minWidth: 40 }}><Business fontSize="small" /></ListItemIcon>
                                        <ListItemText primary="Organisations" primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 600 }} />
                                    </ListItemButton>
                                </ListItem>
                            )}
                            {myOrgs.some(o => o.type === 'PROMOTER' || o.type === 'VENUE') && (
                                <ListItem disablePadding sx={{ mt: 1 }}>
                                    <ListItemButton
                                        onClick={() => navigate('/partner')}
                                        sx={{ borderRadius: 2 }}
                                        selected={location.pathname.startsWith('/partner')}
                                    >
                                        <ListItemIcon sx={{ minWidth: 40 }}><Storefront fontSize="small" /></ListItemIcon>
                                        <ListItemText primary="Promoter & venue" primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 600 }} />
                                    </ListItemButton>
                                </ListItem>
                            )}

                            {artists?.map(artist => {
                                const isExpanded = expandedArtistId === artist.id;

                                // Filter upcoming and past events
                                const today = moment().startOf('day');
                                const allEvents = (artist.events_on_artist || []).sort((a, b) => moment(a.date).diff(moment(b.date)));

                                const filteredAllEvents = allEvents.filter(e => {
                                    // Tour filter
                                    if (tourFilter && e.tour?.id !== tourFilter) return false;
                                    // Type group filter
                                    if (eventTypeFilter === 'ALL') return true;
                                    const group = getEventGroup(e.type);
                                    return group === eventTypeFilter;
                                });

                                const upcomingEvents = filteredAllEvents.filter(e => moment(e.date).isSameOrAfter(today));
                                const pastEvents = filteredAllEvents.filter(e => moment(e.date).isBefore(today));

                                // Only process tours for upcoming events to keep main view clean
                                const toursMap = {};
                                const standaloneEvents = [];

                                upcomingEvents.forEach(event => {
                                    if (event.tour) {
                                        if (!toursMap[event.tour.id]) {
                                            toursMap[event.tour.id] = { ...event.tour, events: [] };
                                        }
                                        toursMap[event.tour.id].events.push(event);
                                    } else {
                                        standaloneEvents.push(event);
                                    }
                                });

                                const tours = Object.values(toursMap).sort((a, b) =>
                                    moment(a.events[0].date).diff(moment(b.events[0].date))
                                );

                                // Create a unified list of items (tours + standalone events) sorted by date
                                const sidebarItems = [];
                                tours.forEach(tour => {
                                    sidebarItems.push({ type: 'tour', data: tour, sortDate: moment(tour.events[0].date) });
                                });

                                // Group consecutive standalone events with the same name
                                const groupedStandalone = [];
                                let currentGroup = null;
                                standaloneEvents.sort((a, b) => moment(a.date).diff(moment(b.date))).forEach(event => {
                                    const eventName = event.name || event.venue?.name || 'Event';
                                    if (currentGroup && currentGroup.name === eventName) {
                                        currentGroup.events.push(event);
                                    } else {
                                        if (currentGroup) groupedStandalone.push(currentGroup);
                                        currentGroup = { name: eventName, events: [event] };
                                    }
                                });
                                if (currentGroup) groupedStandalone.push(currentGroup);

                                groupedStandalone.forEach(group => {
                                    if (group.events.length > 1) {
                                        sidebarItems.push({ type: 'event-group', data: group, sortDate: moment(group.events[0].date), _idx: sidebarItems.length });
                                    } else {
                                        sidebarItems.push({ type: 'event', data: group.events[0], sortDate: moment(group.events[0].date) });
                                    }
                                });
                                sidebarItems.sort((a, b) => a.sortDate.diff(b.sortDate));

                                // Check if user has merchandise access
                                const myCrew = artist.crewMembers_on_artist?.find(cm => cm.user?.id === auth.currentUser?.uid);
                                const hasMerchAccess = myCrew?.merchandise >= 1;

                                return (
                                    <Box key={artist.id}>
                                        <ListItem disablePadding sx={{ mb: 0.25 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', pr: 1, width: '100%' }}>
                                                <ListItemButton
                                                    selected={selectedArtistId === artist.id && !location.pathname.includes('/tours/') && !location.pathname.includes('/event/')}
                                                    onClick={() => handleArtistClick(artist.id)}
                                                    sx={{
                                                        borderRadius: 2,
                                                        flexGrow: 1,
                                                        minWidth: 0,
                                                        '&.Mui-selected': { bgcolor: mode === 'dark' ? 'rgba(255,255,255,0.1)' : '#dde3ea' }
                                                    }}
                                                >
                                                    <ListItemText
                                                        primary={artist.name}
                                                        primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 600, noWrap: true }}
                                                        sx={{ minWidth: 0 }}
                                                    />
                                                    {renderInviteBadge(pendingInvitesByArtistId[artist.id])}
                                                </ListItemButton>
                                                <IconButton
                                                    size="small"
                                                    onClick={handleFilterClick}
                                                    sx={{ ml: 0.5, color: (eventTypeFilter !== 'ALL' || tourFilter) ? 'primary.main' : 'inherit' }}
                                                >
                                                    <FilterList fontSize="small" />
                                                </IconButton>
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setExpandedArtistId(prev => prev === artist.id ? null : artist.id);
                                                    }}
                                                    sx={{ ml: 0.5 }}
                                                >
                                                    {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                                                </IconButton>
                                            </Box>
                                        </ListItem>
                                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                            <List component="div" disablePadding>
                                                {/* PREVIOUS SHOWS SECTION */}
                                                {pastEvents.length > 0 && (
                                                    <Box sx={{ mb: 1 }}>
                                                        <ListItemButton
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setShowPreviousEvents(prev => ({ ...prev, [artist.id]: !prev[artist.id] }));
                                                            }}
                                                            sx={{
                                                                pl: 4,
                                                                py: 0.5,
                                                                minHeight: 32,
                                                                bgcolor: showPreviousEvents[artist.id] ? 'action.hover' : 'transparent'
                                                            }}
                                                        >
                                                            <ListItemIcon sx={{ minWidth: 24, mr: 1, color: 'text.secondary' }}>
                                                                <History fontSize="inherit" />
                                                            </ListItemIcon>
                                                            <ListItemText
                                                                primary="Previous Shows"
                                                                primaryTypographyProps={{ fontSize: '0.85rem', fontWeight: 600, color: 'text.secondary' }}
                                                            />
                                                            {showPreviousEvents[artist.id] ? <ExpandLess fontSize="small" sx={{ width: 16, height: 16, color: 'text.secondary' }} /> : <ExpandMore fontSize="small" sx={{ width: 16, height: 16, color: 'text.secondary' }} />}
                                                        </ListItemButton>

                                                        <Collapse in={showPreviousEvents[artist.id]} timeout="auto" unmountOnExit>
                                                            <List component="div" disablePadding>
                                                                {pastEvents.map(event => {
                                                                    const isEventActive = location.pathname.startsWith(`/event/${event.id}`);
                                                                    return (
                                                                        <Box key={event.id}>
                                                                            <ListItemButton
                                                                                key={event.id}
                                                                                sx={{
                                                                                    pl: 6,
                                                                                    borderRadius: 2,
                                                                                    py: 0.5,
                                                                                    bgcolor: isEventActive ? (mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)') : 'transparent'
                                                                                }}
                                                                                onClick={() => handleEventClick(event.id)}
                                                                                selected={location.pathname === (`/event/${event.id}`)}
                                                                            >
                                                                                <ListItemIcon sx={{ minWidth: 24, mr: 1, color: getStatusColor(event.status), fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                                    {getEventIcon(event.type)}
                                                                                </ListItemIcon>
                                                                                <ListItemText
                                                                                    primary={`${moment(event.date).format("D.M.YY")} - ${formatVenueInfo(event)}`}
                                                                                    primaryTypographyProps={{
                                                                                        fontSize: '0.8rem',
                                                                                        fontWeight: isEventActive ? 600 : 400,
                                                                                        color: isEventActive ? 'primary.main' : 'text.secondary',
                                                                                        noWrap: true
                                                                                    }}
                                                                                    sx={{ minWidth: 0 }}
                                                                                />
                                                                            </ListItemButton>
                                                                            {/* Event Sub-menu (Only if Event is Active) */}
                                                                            <Collapse in={isEventActive} timeout="auto" unmountOnExit>
                                                                                <List component="div" disablePadding>
                                                                                    {getEventSections(event.type).map((subItem) => {
                                                                                        const fullPath = `/event/${event.id}${subItem.path}`;
                                                                                        return (
                                                                                            <ListItemButton
                                                                                                key={subItem.label}
                                                                                                sx={{ pl: 8, py: 0.5, minHeight: 28 }}
                                                                                                selected={location.pathname === fullPath}
                                                                                                onClick={() => navigate(fullPath)}
                                                                                            >
                                                                                                <ListItemIcon sx={{ minWidth: 24, mr: 1, color: 'text.secondary' }}>
                                                                                                    <subItem.Icon fontSize="inherit" />
                                                                                                </ListItemIcon>
                                                                                                <ListItemText
                                                                                                    primary={subItem.label}
                                                                                                    primaryTypographyProps={{ fontSize: '0.75rem', color: 'text.secondary' }}
                                                                                                />
                                                                                            </ListItemButton>
                                                                                        )
                                                                                    })}
                                                                                </List>
                                                                            </Collapse>
                                                                        </Box>
                                                                    );
                                                                })}
                                                            </List>
                                                        </Collapse>
                                                    </Box>
                                                )}


                                                {sidebarItems.map(item => {
                                                    if (item.type === 'tour') {
                                                        const tour = item.data;
                                                        const isTourExpanded = expandedTourIds[tour.id] ?? true;
                                                        const isTourActive = location.pathname.includes(`/tours/${tour.id}`);

                                                        return (
                                                            <Box key={`tour-${tour.id}`}>
                                                                <ListItemButton
                                                                    sx={{ pl: 4, py: 0.5, minHeight: 32 }}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        navigate(`/artist/${artist.id}/tours/${tour.id}`);
                                                                    }}
                                                                    selected={location.pathname === `/artist/${artist.id}/tours/${tour.id}`}
                                                                >
                                                                    <ListItemText
                                                                        primary={tour.name}
                                                                        primaryTypographyProps={{
                                                                            fontSize: '0.85rem',
                                                                            fontWeight: 600,
                                                                            color: isTourActive ? 'primary.main' : 'text.secondary',
                                                                            noWrap: true
                                                                        }}
                                                                    />
                                                                    <IconButton
                                                                        size="small"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            setExpandedTourIds(prev => ({ ...prev, [tour.id]: !isTourExpanded }));
                                                                        }}
                                                                        sx={{ p: 0.5, mr: -1 }}
                                                                    >
                                                                        {isTourExpanded ?
                                                                            <ExpandLess fontSize="small" sx={{ width: 16, height: 16, color: 'text.secondary' }} /> :
                                                                            <ExpandMore fontSize="small" sx={{ width: 16, height: 16, color: 'text.secondary' }} />
                                                                        }
                                                                    </IconButton>
                                                                </ListItemButton>
                                                                <Collapse in={isTourExpanded} timeout="auto" unmountOnExit>
                                                                    <List component="div" disablePadding>
                                                                        {/* Tour Sub-menu (Only if Tour is Active/Selected context) */}
                                                                        {isTourActive && (
                                                                            <>
                                                                                {[
                                                                                    { label: 'Events', path: '', icon: <CalendarMonth fontSize="inherit" /> },
                                                                                    { label: 'Expenses', path: '/expenses', icon: <AttachMoney fontSize="inherit" /> },
                                                                                    { label: 'Overview', path: '/stats', icon: <BarChart fontSize="inherit" /> },
                                                                                    { label: 'Crew Members', path: '/crew', icon: <Group fontSize="inherit" /> },
                                                                                    { label: 'Transportation', path: '/nightliner', icon: <DirectionsBus fontSize="inherit" /> },
                                                                                    { label: 'Accommodation', path: '/accommodation', icon: <Hotel fontSize="inherit" /> },
                                                                                    { label: 'Crew Planning', path: '/matrix', icon: <GridOn fontSize="inherit" /> },
                                                                                ].map((subItem) => {
                                                                                    const fullPath = `/artist/${artist.id}/tours/${tour.id}${subItem.path}`;
                                                                                    return (
                                                                                        <ListItemButton
                                                                                            key={subItem.label}
                                                                                            sx={{ pl: 6, py: 0.5, minHeight: 28 }}
                                                                                            selected={location.pathname === fullPath}
                                                                                            onClick={() => navigate(fullPath)}
                                                                                        >
                                                                                            <ListItemIcon sx={{ minWidth: 24, mr: 1, color: 'text.secondary' }}>
                                                                                                {subItem.icon}
                                                                                            </ListItemIcon>
                                                                                            <ListItemText
                                                                                                primary={subItem.label}
                                                                                                primaryTypographyProps={{ fontSize: '0.8rem', color: 'text.secondary' }}
                                                                                            />
                                                                                        </ListItemButton>
                                                                                    );
                                                                                })}
                                                                            </>
                                                                        )}

                                                                        {/* Events List */}
                                                                        {tour.events.map(event => {
                                                                            const isEventActive = location.pathname.startsWith(`/event/${event.id}`);
                                                                            return (
                                                                                <Box key={event.id}>
                                                                                    <ListItemButton
                                                                                        sx={{ pl: 6, borderRadius: 2, py: 0.5 }}
                                                                                        onClick={() => handleEventClick(event.id)}
                                                                                        selected={location.pathname === (`/event/${event.id}`)}
                                                                                    >
                                                                                        <ListItemIcon sx={{ minWidth: 24, mr: 1, color: getStatusColor(event.status), fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                                            {getEventIcon(event.type)}
                                                                                        </ListItemIcon>
                                                                                        <ListItemText
                                                                                            primary={`${moment(event.date).format("D.M.")} - ${formatVenueInfo(event)}`}
                                                                                            primaryTypographyProps={{
                                                                                                fontSize: '0.8rem',
                                                                                                fontWeight: isEventActive ? 600 : 400,
                                                                                                color: isEventActive ? 'primary.main' : 'text.secondary',
                                                                                                noWrap: true
                                                                                            }}
                                                                                            sx={{ minWidth: 0 }}
                                                                                        />
                                                                                        {renderInviteBadge(pendingInvitesByEventId[event.id])}
                                                                                    </ListItemButton>
                                                                                    {/* Event Sub-menu (Only if Event is Active) */}
                                                                                    <Collapse in={isEventActive} timeout="auto" unmountOnExit>
                                                                                        <List component="div" disablePadding>
                                                                                            {getEventSections(event.type).map((subItem) => {
                                                                                                const fullPath = `/event/${event.id}${subItem.path}`;
                                                                                                return (
                                                                                                    <ListItemButton
                                                                                                        key={subItem.label}
                                                                                                        sx={{ pl: 8, py: 0.5, minHeight: 28 }}
                                                                                                        selected={location.pathname === fullPath}
                                                                                                        onClick={() => navigate(fullPath)}
                                                                                                    >
                                                                                                        <ListItemIcon sx={{ minWidth: 24, mr: 1, color: 'text.secondary' }}>
                                                                                                            <subItem.Icon fontSize="inherit" />
                                                                                                        </ListItemIcon>
                                                                                                        <ListItemText
                                                                                                            primary={subItem.label}
                                                                                                            primaryTypographyProps={{ fontSize: '0.75rem', color: 'text.secondary' }}
                                                                                                        />
                                                                                                    </ListItemButton>
                                                                                                )
                                                                                            })}
                                                                                        </List>
                                                                                    </Collapse>
                                                                                </Box>
                                                                            );
                                                                        })}
                                                                    </List>
                                                                </Collapse>
                                                            </Box>
                                                        );
                                                    } else if (item.type === 'event-group') {
                                                        // Grouped events with same name
                                                        const group = item.data;
                                                        const groupKey = `${artist.id}-${group.name}`;
                                                        const hasActiveEvent = group.events.some(e => location.pathname.startsWith(`/event/${e.id}`));
                                                        const isGroupExpanded = expandedGroupNames[groupKey] ?? hasActiveEvent;
                                                        const firstDate = moment(group.events[0].date).format('D.M.');
                                                        const lastDate = moment(group.events[group.events.length - 1].date).format('D.M.');
                                                        const firstEventIcon = getEventIcon(group.events[0].type);
                                                        const firstEventStatus = group.events[0].status;

                                                        return (
                                                            <Box key={`group-${groupKey}-${item._idx ?? 0}`}>
                                                                <ListItemButton
                                                                    sx={{ pl: 4, py: 0.5, minHeight: 32 }}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setExpandedGroupNames(prev => ({ ...prev, [groupKey]: !isGroupExpanded }));
                                                                    }}
                                                                >
                                                                    <ListItemIcon sx={{ minWidth: 24, mr: 1, color: getStatusColor(firstEventStatus), fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                        {firstEventIcon}
                                                                    </ListItemIcon>
                                                                    <ListItemText
                                                                        primary={`${group.name}`}
                                                                        secondary={`${firstDate} – ${lastDate}`}
                                                                        primaryTypographyProps={{
                                                                            fontSize: '0.85rem',
                                                                            fontWeight: hasActiveEvent ? 600 : 500,
                                                                            color: hasActiveEvent ? 'primary.main' : 'text.secondary',
                                                                            noWrap: true
                                                                        }}
                                                                        secondaryTypographyProps={{
                                                                            fontSize: '0.7rem',
                                                                            color: 'text.disabled',
                                                                        }}
                                                                    />
                                                                    <Badge
                                                                        badgeContent={group.events.length}
                                                                        color="default"
                                                                        sx={{
                                                                            mr: 1,
                                                                            '& .MuiBadge-badge': {
                                                                                position: 'relative',
                                                                                transform: 'none',
                                                                                fontSize: '0.65rem',
                                                                                height: 18,
                                                                                minWidth: 18,
                                                                                bgcolor: mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
                                                                                color: 'text.secondary',
                                                                            }
                                                                        }}
                                                                    />
                                                                    {isGroupExpanded ?
                                                                        <ExpandLess fontSize="small" sx={{ width: 16, height: 16, color: 'text.secondary' }} /> :
                                                                        <ExpandMore fontSize="small" sx={{ width: 16, height: 16, color: 'text.secondary' }} />
                                                                    }
                                                                </ListItemButton>
                                                                <Collapse in={isGroupExpanded} timeout="auto" unmountOnExit>
                                                                    <List component="div" disablePadding>
                                                                        {group.events.map(event => {
                                                                            const isEventActive = location.pathname.startsWith(`/event/${event.id}`);
                                                                            return (
                                                                                <Box key={event.id}>
                                                                                    <ListItemButton
                                                                                        sx={{ pl: 6, borderRadius: 2, py: 0.5 }}
                                                                                        onClick={() => handleEventClick(event.id)}
                                                                                        selected={location.pathname === (`/event/${event.id}`)}
                                                                                    >
                                                                                        <ListItemIcon sx={{ minWidth: 24, mr: 1, color: getStatusColor(event.status), fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                                            {getEventIcon(event.type)}
                                                                                        </ListItemIcon>
                                                                                        <ListItemText
                                                                                            primary={`${moment(event.date).format("D.M.")} - ${formatVenueInfo(event)}`}
                                                                                            primaryTypographyProps={{
                                                                                                fontSize: '0.8rem',
                                                                                                fontWeight: isEventActive ? 600 : 400,
                                                                                                color: isEventActive ? 'primary.main' : 'text.secondary',
                                                                                                noWrap: true
                                                                                            }}
                                                                                            sx={{ minWidth: 0 }}
                                                                                        />
                                                                                        {renderInviteBadge(pendingInvitesByEventId[event.id])}
                                                                                    </ListItemButton>
                                                                                    {/* Event Sub-menu (Only if Event is Active) */}
                                                                                    <Collapse in={isEventActive} timeout="auto" unmountOnExit>
                                                                                        <List component="div" disablePadding>
                                                                                            {getEventSections(event.type).map((subItem) => {
                                                                                                const fullPath = `/event/${event.id}${subItem.path}`;
                                                                                                return (
                                                                                                    <ListItemButton
                                                                                                        key={subItem.label}
                                                                                                        sx={{ pl: 8, py: 0.5, minHeight: 28 }}
                                                                                                        selected={location.pathname === fullPath}
                                                                                                        onClick={() => navigate(fullPath)}
                                                                                                    >
                                                                                                        <ListItemIcon sx={{ minWidth: 24, mr: 1, color: 'text.secondary' }}>
                                                                                                            <subItem.Icon fontSize="inherit" />
                                                                                                        </ListItemIcon>
                                                                                                        <ListItemText
                                                                                                            primary={subItem.label}
                                                                                                            primaryTypographyProps={{ fontSize: '0.75rem', color: 'text.secondary' }}
                                                                                                        />
                                                                                                    </ListItemButton>
                                                                                                )
                                                                                            })}
                                                                                        </List>
                                                                                    </Collapse>
                                                                                </Box>
                                                                            );
                                                                        })}
                                                                    </List>
                                                                </Collapse>
                                                            </Box>
                                                        );
                                                    } else {
                                                        // Standalone event
                                                        const event = item.data;
                                                        const isEventActive = location.pathname.startsWith(`/event/${event.id}`);
                                                        return (
                                                            <Box key={event.id}>
                                                                <ListItemButton
                                                                    key={event.id}
                                                                    sx={{ pl: 4, borderRadius: 2, py: 0.5 }}
                                                                    onClick={() => handleEventClick(event.id)}
                                                                    selected={location.pathname === (`/event/${event.id}`)}
                                                                >
                                                                    <ListItemIcon sx={{ minWidth: 24, mr: 1, color: getStatusColor(event.status), fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                        {getEventIcon(event.type)}
                                                                    </ListItemIcon>
                                                                    <ListItemText
                                                                        primary={`${moment(event.date).format("D.M.")} - ${formatVenueInfo(event)}`}
                                                                        primaryTypographyProps={{
                                                                            fontSize: '0.85rem',
                                                                            fontWeight: isEventActive ? 600 : 400,
                                                                            color: isEventActive ? 'primary.main' : 'text.secondary',
                                                                            noWrap: true
                                                                        }}
                                                                        sx={{ minWidth: 0 }}
                                                                    />
                                                                    {renderInviteBadge(pendingInvitesByEventId[event.id])}
                                                                </ListItemButton>
                                                                <Collapse in={isEventActive} timeout="auto" unmountOnExit>
                                                                    <List component="div" disablePadding>
                                                                        {getEventSections(event.type).map((subItem) => {
                                                                            const fullPath = `/event/${event.id}${subItem.path}`;
                                                                            return (
                                                                                <ListItemButton
                                                                                    key={subItem.label}
                                                                                    sx={{ pl: 8, py: 0.5, minHeight: 28 }}
                                                                                    selected={location.pathname === fullPath}
                                                                                    onClick={() => navigate(fullPath)}
                                                                                >
                                                                                    <ListItemIcon sx={{ minWidth: 24, mr: 1, color: 'text.secondary' }}>
                                                                                        <subItem.Icon fontSize="inherit" />
                                                                                    </ListItemIcon>
                                                                                    <ListItemText
                                                                                        primary={subItem.label}
                                                                                        primaryTypographyProps={{ fontSize: '0.75rem', color: 'text.secondary' }}
                                                                                    />
                                                                                </ListItemButton>
                                                                            )
                                                                        })}
                                                                    </List>
                                                                </Collapse>
                                                            </Box>
                                                        )
                                                    }
                                                })}
                                                {tours.length === 0 && standaloneEvents.length === 0 && (
                                                    <Typography variant="caption" sx={{ pl: 4, py: 1, display: 'block', color: 'text.secondary' }}>
                                                        No upcoming events
                                                    </Typography>
                                                )}
                                            </List>
                                        </Collapse>



                                    </Box>
                                );
                            })}
                        </>
                    )}
                </List>
            </Box>

            {/* Bottom Section */}
            <List sx={{ px: 1 }}>
                <ListItem disablePadding sx={{ mb: 0.5 }}>
                    <ListItemButton
                        sx={{ borderRadius: 2 }}
                        onClick={(e) => setAnchorElTheme(e.currentTarget)}
                    >
                        <ListItemIcon sx={{ minWidth: 40 }}><Settings fontSize="small" /></ListItemIcon>
                        <ListItemText primary="Settings" primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 500 }} />
                    </ListItemButton>
                </ListItem>
                <ListItem disablePadding sx={{ mt: 1 }}>
                    <ListItemButton
                        onClick={(e) => setAnchorElUser(e.currentTarget)}
                        sx={{ borderRadius: 2, py: 1 }}
                    >
                        <Avatar
                            src={auth.currentUser?.photoURL}
                            sx={{ width: 28, height: 28, mr: 1.5 }}
                        >
                            {auth.currentUser?.displayName?.[0]}
                        </Avatar>
                        <Box sx={{ overflow: 'hidden' }}>
                            <Typography variant="body2" noWrap fontWeight={500}>
                                {auth.currentUser?.displayName || 'User'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" noWrap display="block">
                                {auth.currentUser?.email}
                            </Typography>
                        </Box>
                    </ListItemButton>
                </ListItem>
            </List>

            <Menu
                anchorEl={filterAnchorEl}
                open={Boolean(filterAnchorEl)}
                onClose={() => handleFilterClose()}
                PaperProps={{
                    elevation: 3,
                    sx: { width: 240, borderRadius: 3, mt: 1 }
                }}
            >
                <Typography variant="overline" sx={{ px: 2, pt: 1, display: 'block', color: 'text.secondary', fontWeight: 700 }}>Type</Typography>
                <MenuItem onClick={(e) => { e.stopPropagation(); handleFilterClose('ALL'); }} selected={eventTypeFilter === 'ALL'}>
                    <ListItemText primary="All Events" />
                </MenuItem>
                {Object.values(EventTypeGroup).filter(g => g.key !== 'OTHER').map(group => (
                    <MenuItem
                        key={group.key}
                        onClick={(e) => { e.stopPropagation(); handleFilterClose(group.key); }}
                        selected={eventTypeFilter === group.key}
                    >
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: group.color, mr: 1.5, flexShrink: 0 }} />
                        <ListItemText primary={group.name} />
                    </MenuItem>
                ))}

                <Divider sx={{ my: 1 }} />
                <Typography variant="overline" sx={{ px: 2, display: 'block', color: 'text.secondary', fontWeight: 700 }}>Tour</Typography>
                <MenuItem onClick={(e) => { e.stopPropagation(); handleTourFilterChange(null); }} selected={tourFilter === null}>
                    <ListItemText primary="All Tours" />
                </MenuItem>
                {(() => {
                    // Build unique tour list from all artists' events
                    const tourMap = {};
                    artists?.forEach(artist => {
                        (artist.events_on_artist || []).forEach(event => {
                            if (event.tour && !tourMap[event.tour.id]) {
                                tourMap[event.tour.id] = event.tour.name || 'Unnamed Tour';
                            }
                        });
                    });
                    return Object.entries(tourMap).map(([id, name]) => (
                        <MenuItem
                            key={id}
                            onClick={(e) => { e.stopPropagation(); handleTourFilterChange(id); }}
                            selected={tourFilter === id}
                        >
                            <ListItemIcon sx={{ minWidth: 28 }}><DirectionsBus fontSize="inherit" /></ListItemIcon>
                            <ListItemText primary={name} primaryTypographyProps={{ noWrap: true, fontSize: '0.85rem' }} />
                        </MenuItem>
                    ));
                })()}
            </Menu>

            {/* User Menu - Attached to Bottom Section Logic */}
            <Menu
                anchorEl={anchorElUser}
                open={Boolean(anchorElUser)}
                onClose={() => { setAnchorElUser(null); setAnchorElTheme(null); }}
                PaperProps={{
                    elevation: 3,
                    sx: { width: 250, borderRadius: 3, mb: 1 }
                }}
                transformOrigin={{ horizontal: 'left', vertical: 'bottom' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
                <MenuItem onClick={() => { setAnchorElUser(null); navigate('/profile'); }} sx={{ py: 1.5 }}>
                    <ListItemIcon><Person fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Profile" />
                </MenuItem>
                <MenuItem onClick={() => { setAnchorElUser(null); navigate('/profile'); }} sx={{ py: 1.5 }}>
                    <ListItemIcon><CalendarMonth fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Calendar Subscription" />
                </MenuItem>
                <MenuItem onClick={() => { setAnchorElUser(null); navigate('/org'); }} sx={{ py: 1.5 }}>
                    <ListItemIcon><Business fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Organisations" />
                </MenuItem>
                <Divider />
                <MenuItem onClick={handleLogout} sx={{ py: 1.5 }}>
                    <ListItemIcon><Logout fontSize="small" /></ListItemIcon>
                    <ListItemText primary="Log out" />
                </MenuItem>
                <Divider />
                <Box sx={{ px: 2, py: 1 }}>
                    <Typography variant="caption" color="text.disabled" display="block">
                        App v{packageInfo.version} · OTA {appVersion?.version || '—'}
                    </Typography>
                </Box>
            </Menu>

            {/* Theme Submenu */}
            <Menu
                anchorEl={anchorElTheme}
                open={Boolean(anchorElTheme)}
                onClose={() => setAnchorElTheme(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{
                    elevation: 3,
                    sx: { width: 180, borderRadius: 3, ml: 1 }
                }}
            >
                <MenuItem onClick={() => { setTheme('light'); setAnchorElTheme(null); }}>
                    <ListItemIcon><Radio checked={mode === 'light'} size="small" /></ListItemIcon>
                    Light
                </MenuItem>
                <MenuItem onClick={() => { setTheme('dark'); setAnchorElTheme(null); }}>
                    <ListItemIcon><Radio checked={mode === 'dark'} size="small" /></ListItemIcon>
                    Dark
                </MenuItem>
            </Menu>
        </Box >
    );
};

export default Sidebar;